import { HTMLAttributes, ReactNode, useState } from 'react';
import styled from 'styled-components';

import { ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Tooltip } from './Tooltip';

const MARKER_SIZE = 24;

const Row = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: ${MARKER_SIZE}px 1fr;
	column-gap: ${tokens.gap.element}px;
	padding-bottom: ${tokens.gap.list}px;

	&:not(:last-child):before {
		content: '';
		position: absolute;
		left: ${MARKER_SIZE / 2}px;
		top: ${MARKER_SIZE + 4}px;
		bottom: 4px;
		width: 1.5px;
		background: ${tokens.border.subtle};
	}
`;

const MarkerSlot = styled.div<{ $color?: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${MARKER_SIZE}px;
	height: ${MARKER_SIZE}px;
	color: ${(props) => props.$color ?? tokens.icon.secondary};

	svg {
		width: 20px;
		height: 20px;
	}
`;

const Content = styled.div`
	min-width: 0;
`;

const Text = styled.div`
	min-height: ${MARKER_SIZE}px;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	column-gap: ${tokens.gap.inline}px;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.body};
`;

const Meta = styled.span`
	color: ${tokens.text.tertiary};
	white-space: nowrap;
	cursor: default;

	&:before {
		content: '·';
		margin-right: ${tokens.gap.inline}px;
	}
`;

const Toggle = styled.button<{ $open: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 2px;
	margin-top: 2px;
	padding: 0;
	border: 0;
	background: none;
	cursor: pointer;
	color: ${tokens.brand.text};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};

	svg {
		width: 16px;
		height: 16px;
		transition: transform 0.2s ease;
		transform: rotate(${(props) => (props.$open ? 90 : 0)}deg);
	}
`;

const QuoteBlock = styled.div`
	margin-top: ${tokens.gap.inline}px;
	border-left: 3px solid ${tokens.border.default};
	padding-left: ${tokens.padding.control}px;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-style: italic;
	line-height: ${tokens.typography.lineHeights.body};
	white-space: pre-wrap;
	word-break: break-word;

	& + & {
		margin-top: 0;
	}
`;

function Marker(props: { color?: string; children?: ReactNode }) {
	return <MarkerSlot $color={props.color}>{props.children}</MarkerSlot>;
}

function Details(props: { label: string; children: ReactNode }) {
	const [open, setOpen] = useState(false);

	return (
		<div>
			<Toggle type="button" $open={open} aria-expanded={open} onClick={() => setOpen(!open)}>
				<ChevronRightIcon />
				{props.label}
			</Toggle>
			{open && props.children}
		</div>
	);
}

function Quote(props: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
	const { children, ...rest } = props;
	return <QuoteBlock {...rest}>{children}</QuoteBlock>;
}

type ActivityRowProps = {
	marker: ReactNode;
	children: ReactNode;
	meta?: ReactNode;
	metaTooltip?: ReactNode;
	details?: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

/**
 * Timeline row: marker on the left, a vertical line to the next sibling row,
 * sentence and optional relative time on the right.
 * @param props component props
 * @param props.marker avatar or icon on the left
 * @param props.children the sentence
 * @param props.meta relative time
 * @param props.metaTooltip absolute time shown in a tooltip
 * @param props.details expandable extras under the sentence
 */
export function ActivityRow(props: ActivityRowProps) {
	const { marker, children, meta, metaTooltip, details, ...rest } = props;
	const metaNode = meta != null && meta !== '' && <Meta>{meta}</Meta>;

	return (
		<Row {...rest}>
			{marker}
			<Content>
				<Text>
					<span>{children}</span>
					{metaNode &&
						(metaTooltip != null && metaTooltip !== '' ? (
							<Tooltip content={metaTooltip} dark position="right" portal fitContent>
								{metaNode}
							</Tooltip>
						) : (
							metaNode
						))}
				</Text>
				{details}
			</Content>
		</Row>
	);
}

ActivityRow.Marker = Marker;
ActivityRow.Details = Details;
ActivityRow.Quote = Quote;
